/**
 * Why a level will not open.
 *
 * There have always been two answers and the code only had room for one.
 * `isUnlocked` (progress.ts) answers a question about the *path*: is every
 * level this one is built on behind you. The paid half asks a question about
 * the *account*: have you bought this. They are different refusals with
 * different fixes, and a learner told the wrong one is a learner who goes
 * looking for a level 19 they already cleared.
 *
 * This composes the two rather than changing either, which is deliberate:
 * `isUnlocked` is load-bearing in four places and pinned by the progress
 * tests, and the sequence rule is not the thing this file decides.
 *
 * ## What a refusal has to carry now
 *
 * The sequence rule used to be "the level numbered one lower is cleared", and
 * the word `sequence` said the whole of it: there was exactly one
 * prerequisite, it was always the row above, and every sentence about it could
 * be written by subtracting one. A mission now names its own prerequisites
 * (`requires`, docs/curriculum.md §5), there can be several, and they need not
 * be anywhere near this level's number — an Act III entry level is built on
 * the last level of the general track and on nothing else. So the refusal
 * carries the missions that are actually in the way, and the copy names them
 * instead of computing them.
 *
 * A note on the failure mode that used to be here, because the shape of the
 * catalogue still depends on it. `isUnlocked` fails *closed*: a prerequisite
 * that cannot be found in the catalogue refuses rather than opens. It did the
 * opposite for most of the course's life, and that would have been a hole if
 * the paid half were implemented by *removing* levels, because removing level
 * 20 would have opened level 21. It is not: `missions.json` carries all
 * fifty-two in level order and the paid ones arrive as locked stubs
 * (scripts/build-missions.mjs), so the prerequisite is never missing and the
 * question never comes up. That is the main reason the split was built as
 * stubs instead of omissions, and it is still the reason, even though the
 * gate would now survive the other choice.
 */
import { isBehind, isUnlocked, levelProgress, type Progress } from '../progress';
import { getStrings } from './strings';
import type { PublicMission } from '../types';

/**
 * What stands between a learner and a level.
 *
 * - `open` — play it.
 * - `sequence` — something this level is built on is not behind them yet, and
 *   the gate says which: `missing` is the prerequisites that exist and are
 *   not cleared or skipped, and `unresolved` is the ids that are not in the
 *   catalogue at all. At least one of the two is non-empty whenever this is
 *   the answer, which is what lets the copy promise a sentence.
 * - `paywall` — it is in the paid half and this browser has not been given
 *   it. The mission is a stub: there is no brief and no objectives behind
 *   the card, so this is not a disabled button over content that arrived
 *   anyway.
 *
 * An object rather than the three strings it used to be, because a refusal
 * that cannot name what it is refusing over is the thing that had to change.
 * `missing` carries whole missions rather than level numbers so that the
 * sentence can grow: the copy names levels today, and the day a second track
 * ships with its own numbering it will have to name the track beside the
 * number, which is a change in one file rather than a change in the gate.
 */
export type LevelGate =
  | { kind: 'open' }
  | { kind: 'sequence'; missing: PublicMission[]; unresolved: string[] }
  | { kind: 'signin' }
  | { kind: 'paywall' };

/** A gate that is not `open`: the only kind there is anything to say about. */
export type LockedGate = Exclude<LevelGate, { kind: 'open' }>;

/**
 * The two answers that carry nothing, as single values.
 *
 * The map computes a gate for every row on every render, and two of the three
 * answers are the same object every time. Shared rather than rebuilt, which
 * also means `gateFor(…) === OPEN` would work — nothing relies on that, and
 * nothing should, but a caller that narrows on `kind` is reading the same
 * thing either way.
 */
const OPEN: LevelGate = { kind: 'open' };
const PAYWALL: LevelGate = { kind: 'paywall' };
const SIGNIN: LevelGate = { kind: 'signin' };

/**
 * The last level anybody may play without an account.
 *
 * A mission id rather than a number, the way `FREE_THROUGH` names the paywall
 * boundary, so that inserting a level does not silently move the wall.
 *
 * Where it sits is the biggest single decision in the funnel: everything
 * before it is a stranger typing into a real Linux machine one click after
 * landing, and everything after it is an account. It is the last level
 * where Iris is scripted (engine iris-script.ts): levels 0-4, about half an
 * hour, all of it open with Iris at no cost, so the first account is asked
 * for where the live agent, which spends money, begins (owner, 2 October
 * 2026; docs/agent-first-plan.md). It was level 1 before that.
 */
export const TRY_THROUGH = 'l17-who-broke-it';

/**
 * Whether this level can be played with no account at all.
 *
 * **It fails closed.** A catalogue that has lost the boundary level asks for an
 * account everywhere rather than opening everywhere: the failure that costs a
 * sign-up is recoverable, and the one that hands out the paid half is not.
 */
export function playableSignedOut(mission: PublicMission, missions: PublicMission[]): boolean {
  if (mission.track !== 'general' || mission.locked) return false;
  const boundary = missions.find((m) => m.id === TRY_THROUGH);
  if (!boundary) return false;
  return mission.level <= boundary.level;
}

/**
 * The paywall is checked first. Someone who has not bought level 34 does not
 * need to be told to go and clear level 33: that is true, and it is not the
 * thing standing in their way.
 *
 * It matters more now than it did, not less. A locked stub carries its
 * `requires` like every other mission (engine/missions.ts), so the sequence
 * branch below would happily compute a list of prerequisites for a level this
 * account does not hold — a correct answer to a question nobody asked, in
 * front of the one screen in the product that asks for money.
 */
export function gateFor(
  progress: Progress,
  mission: PublicMission,
  missions: PublicMission[],
  signedIn: boolean,
): LevelGate {
  if (mission.locked) return PAYWALL;
  // Before the sequence, for the reason the paywall comes before both:
  // somebody signed out does not need to be told to go and clear level four,
  // because they cannot reach level four either. One sentence, and it is the
  // one that is actually standing in their way.
  if (!signedIn && !playableSignedOut(mission, missions)) return SIGNIN;
  if (isUnlocked(progress, mission, missions)) return OPEN;
  return { kind: 'sequence', ...unmet(progress, mission, missions) };
}

/**
 * The prerequisites that are not behind this learner, split by whether the
 * catalogue could find them.
 *
 * This re-reads the rule `isUnlocked` applies rather than asking it, and the
 * duplication is deliberate: `isUnlocked(prerequisite)` asks whether that
 * level is *playable*, which is a different question from whether it is
 * *behind* you — a skipped level is behind you and a paid one may be playable
 * and not cleared. Both halves read the same two helpers (`levelProgress`,
 * `isBehind`), which is what keeps the two answers from disagreeing, and the
 * only way to reach an empty result here is a gate that said `sequence` when
 * `isUnlocked` said yes. The copy still answers that case rather than
 * printing a sentence with a hole in it.
 */
function unmet(
  p: Progress,
  mission: PublicMission,
  missions: PublicMission[],
): { missing: PublicMission[]; unresolved: string[] } {
  const missing: PublicMission[] = [];
  const unresolved: string[] = [];
  for (const id of mission.requires) {
    const prerequisite = missions.find((m) => m.id === id);
    if (!prerequisite) unresolved.push(id);
    else if (!isBehind(levelProgress(p, prerequisite.id))) missing.push(prerequisite);
  }
  return { missing, unresolved };
}

/** Shorthand for the places that only need the yes/no. */
export function canPlay(
  progress: Progress, mission: PublicMission, missions: PublicMission[], signedIn: boolean,
): boolean {
  return gateFor(progress, mission, missions, signedIn).kind === 'open';
}

/** Whether this level is in the paid half at all, bought or not. */
export function isPaid(mission: PublicMission): boolean {
  return mission.locked === true;
}

/**
 * How many levels this browser actually holds, and how many exist. The map's
 * counters and the hero line both want this now that "the course" and "the
 * levels you can play" are different numbers.
 */
export function ownedCount(missions: PublicMission[]): { owned: number; total: number } {
  return { owned: missions.filter((m) => !m.locked).length, total: missions.length };
}

/**
 * The paid levels of one track, as a range, when a range is a true sentence
 * about them. Null when it is not.
 *
 * Two things have to hold before `20-51` means anything, and both are facts
 * about one track rather than about the course:
 *
 * 1. **The locked levels are the end of the lane.** If a free level sits
 *    above a locked one, a range from the first locked level to the last
 *    level of the track sweeps up something the learner already has, and the
 *    offer overstates itself.
 * 2. **The lane has free levels below them.** A range is legible because it
 *    names where the free half stops and the paid half starts. A wholly paid
 *    lane has no such boundary, and — since every track numbers itself from
 *    zero (docs/curriculum.md §5) — its `0-19` is a run of numbers the
 *    learner has already played under a different track's name. That is the
 *    case the old min-to-max sweep turned into a lie rather than a refusal.
 *
 * `to` is the last level of the *track* rather than the last locked one. They
 * are the same number by construction here — that is what condition 1 says —
 * and the sentence means the first one: "unlock levels 20 to the end".
 */
function laneTail(missions: PublicMission[], track: string): { from: number; to: number } | null {
  let from = Infinity;
  let to = -Infinity;
  let topFree = -Infinity;
  let locked = 0;
  for (const m of missions) {
    if (m.track !== track) continue;
    if (m.level > to) to = m.level;
    if (m.locked) {
      locked++;
      if (m.level < from) from = m.level;
    } else if (m.level > topFree) topFree = m.level;
  }
  if (locked === 0) return null;
  if (topFree === -Infinity || topFree > from) return null;
  return { from, to };
}

/**
 * The paid half, as the catalogue in this browser actually reports it: how
 * many levels it is, and where they run from and to when that can be said at
 * all. Null when there are none.
 *
 * Null is the signal the offer block and the invite dialog both hang off, and
 * it is the right one rather than a convenient one. A catalogue with nothing
 * locked in it means either that this account has bought the course or that
 * the free half *is* the course, and in both cases there is nothing to sell
 * and the block should not be on the page. Asking "is anything locked" gets
 * that right in both, where asking "has this account paid" would put an offer
 * in front of somebody on a build that has no paid half at all.
 *
 * ## Why the range is now allowed to be absent
 *
 * This used to be the smallest locked level to the largest level in the
 * course, and it was right for exactly as long as the course was one lane:
 * the free half ends where the first locked level starts, the course ends at
 * its last level, and everything between the two is paid. A specialization
 * track is priced wholly paid and numbers itself from zero, so the moment one
 * ships, that sweep returns `0-51` — which reads as the whole course, half of
 * which the learner was given free.
 *
 * **The count is the fact; the range is a nicety.** `count` is true whatever
 * shape the paid content is in, and it is what the sentence should lean on
 * when the locked levels are spread across lanes. `range` is offered only
 * when every locked level is in one track and `laneTail` above says a range
 * describes them, and it names that track so the sentence can say which lane
 * it is talking about.
 *
 * The numbers come from the catalogue rather than from a constant for the
 * reason they always did: 20 and 51 are true today and are facts about how
 * many chapters have shipped, not about the product.
 */
export interface PaidSpan {
  /** Locked levels in the whole catalogue, across every track. Always true. */
  count: number;
  /** Where they run, when they run anywhere nameable. See above. */
  range: { track: string; from: number; to: number } | null;
}

/** The paid half of this catalogue, as `PaidSpan` above describes it. */
export function paidSpan(missions: PublicMission[]): PaidSpan | null {
  const locked = missions.filter((m) => m.locked);
  if (locked.length === 0) return null;
  const track = locked[0]!.track;
  // One lane, or none: two lanes that are both partly paid have two ranges
  // and a sentence has room for one, so the caller gets the count instead.
  const tail = locked.every((m) => m.track === track) ? laneTail(missions, track) : null;
  return { count: locked.length, range: tail ? { track, ...tail } : null };
}

/**
 * The paid content the learner would have to hold to play *this* level,
 * scoped to the track it is in.
 *
 * Scoped, because the refusal is about one level and the learner is looking
 * at one lane: the numbers in the sentence should be the numbers on the rows
 * around the one they clicked. Levels 0-19 of the backend track and levels
 * 0-19 of the general track are different levels with the same numbers, and a
 * sentence that reaches across both is naming neither.
 *
 * - `tail` — the lane is part free and part paid, and the paid part is the
 *   end of it. The range is the sentence: "unlock levels 20-51".
 * - `whole` — the whole lane is paid. There is no boundary inside it to name
 *   and its numbers collide with every other lane's, so the sentence counts
 *   instead: "this path's twenty levels".
 *
 * The count is seeded from `mission` rather than trusted to the sweep, the
 * way `paidRange` was before it: `mission` is itself a paid level of this
 * lane whenever this is reached, so it is a member of the set, and a
 * `missions` that somehow did not contain it gives back a count of one, which
 * is wrong but is not nonsense — and cannot happen, because the gate that
 * produced this refusal was computed from the same list.
 */
type PaidScope = { kind: 'tail'; from: number; to: number } | { kind: 'whole'; count: number };

function paidScope(missions: PublicMission[], mission: PublicMission): PaidScope {
  const tail = laneTail(missions, mission.track);
  if (tail) return { kind: 'tail', ...tail };
  let count = 0;
  let counted = false;
  for (const m of missions) {
    if (m.track !== mission.track || !m.locked) continue;
    count++;
    if (m.id === mission.id) counted = true;
  }
  return { kind: 'whole', count: counted ? count : count + 1 };
}

/**
 * What to say when somebody clicks a level they cannot open.
 *
 * The sequence message names the levels to clear — the ones the mission
 * actually asks for, in the order the map draws them, rather than the one
 * below it on the screen. The paywall message does not mention clearing
 * anything, because there is nothing to clear: it is the one place in the
 * product that asks for money, and it should read like an offer rather than
 * an error.
 *
 * It takes the mission rather than a level number, and that is not tidying.
 * `level` is unique within a track and no longer across the course, so a
 * number cannot find its own mission any more — and the paywall sentence
 * needs the track to know which lane's numbers it is allowed to quote. The
 * shell should hand a locked row's mission id down for the same reason
 * (docs/curriculum.md §6).
 *
 * `LockedGate` rather than `LevelGate`: an open level has no refusal to
 * render, and passing one used to produce the sequence sentence for a level
 * the learner could have played. Callers narrow on `kind` and the compiler
 * does the rest.
 *
 * `getStrings()`, not the hook: this is called from an effect in App, not
 * from a render, and a hook there would be a hook outside a component.
 */
export function lockedMessage(
  gate: LockedGate,
  mission: PublicMission,
  missions: PublicMission[],
  signedIn = true,
): string {
  const s = getStrings().progress.locked;
  if (gate.kind === 'signin') return s.signin(mission.level);
  if (gate.kind === 'sequence') {
    // Ascending, which is the order they sit in on the map. `requires` is in
    // authoring order, and an author writing the interesting prerequisite
    // first should not decide how the sentence reads.
    const levels = gate.missing.map((m) => m.level).sort((a, b) => a - b);
    return s.sequence(levels, mission.level);
  }
  const scope = paidScope(missions, mission);
  // Somebody signed out is one step further from playing this than somebody
  // who simply has not bought it, and the sentence should say which step is
  // next rather than making them find out by pressing the only button.
  if (scope.kind === 'tail') {
    return signedIn
      ? s.paywall(mission.level, scope.from, scope.to)
      : s.paywallSignedOut(mission.level, scope.from, scope.to);
  }
  return signedIn ? s.paywallTrack(mission.level, scope.count) : s.paywallTrackSignedOut(mission.level, scope.count);
}

/**
 * Where a learner goes next.
 *
 * Three files used to answer this and they already disagreed. Here it was the
 * first uncleared level in catalogue order the gate would open; in the
 * workstation it was the level one slot along in the array, nulled out when
 * that neighbour was a locked stub; in the debrief it was the level whose
 * number was one higher, checked against nothing at all. With one track the
 * three agree by accident — array order, prerequisite order and the integer
 * line are the same order — so the disagreement has been invisible for the
 * whole of the course's life. A second track ends all three coincidences at
 * once, and takes the arithmetic one with it: every track numbers itself from
 * zero, so `level + 1` finds whichever lane sits first in the catalogue
 * (docs/curriculum.md §5, §6).
 *
 * So: one helper, reading `requires` rather than positions, and it answers
 * with a **set**. That is not hedging. After the last level of the general
 * track several lanes open together and each of them is a true answer to
 * "what now" — a function that returned a single mission there would be
 * choosing somebody's specialization for them and hiding that it had.
 *
 * Two questions, one shape:
 *
 * - **What does this level open?** `from` is a mission, and the answer is the
 *   missions that name it in their `requires`. The workstation, the clear
 *   overlay and the debrief all ask this: each is standing on one level and
 *   pointing past it.
 * - **Where has this learner got to?** `from` is null, and the answer is every
 *   level that is open and not yet cleared — the frontier. The map asks this,
 *   because the map is not standing anywhere. It has been a set here for as
 *   long as skipping has existed: somebody who skipped level 3 and cleared 4
 *   and 5 has two open uncleared levels, and this file has always quietly
 *   taken the first of them.
 *
 * ## Two lists, because the callers really do differ
 *
 * `open` is what this browser can play. `barred` is a level that exists and
 * whose prerequisites are behind, held back by the paywall alone. The split
 * is what lets the workstation and the debrief keep the opposite behaviours
 * they have always had and both be right: a Next button that lands on a level
 * which cannot open would spend the best moment in the course on an error
 * screen, while the foot of a debrief is a table of contents, and pointing at
 * level 20 from level 19 is how a reader finds out the course continues.
 * Neither was a bug. They are different sentences about the same fact, and a
 * helper that returned one list would have had to break one of them.
 *
 * A level whose *other* prerequisites are still outstanding is in neither
 * list. It is not next: something else has to happen first, and where that is
 * said is the map, one row at a time, with `lockedMessage` naming it.
 *
 * ## `from` counts as behind
 *
 * A successor is judged as though `from` were already cleared, because by the
 * time one of these links is followed it is: the clear overlay opens on a
 * clear, and the Skip link records a skip on its way out — and a skip
 * satisfies a prerequisite, which is the one thing a skip does (progress.ts).
 * Judging `from` as it stands at render would point the Skip link on an
 * unfinished level at the map, which is the single place the learner leaving
 * that level does not want to be sent.
 *
 * ## The rule, where there is room for exactly one
 *
 * The hero on the map, the Next button on the rail, the button on the clear
 * overlay and the foot of the debrief each have room for one level, and the
 * rule is the same in all four: **take the head of the set.** The set comes
 * back ordered so that the head is a continuation rather than a choice — a
 * unfinished checkpoint for the chapter just played comes first, followed by
 * successors in the same track, then other tracks in catalogue order.
 *
 * The reasoning is that a fork is a decision and a decision belongs on the
 * map, where every lane is drawn beside what it teaches and none of them is
 * pre-pressed. A button that silently picked one of six would be making that
 * decision on the learner's behalf in the four seconds after a clear, which
 * is the moment they are least able to notice it being made. So the button
 * keeps doing the one thing a button is good at — forward — and when forward
 * means a new world, which is exactly what a branch produces, the copy
 * already names it rather than numbering it: the overlay and the rail both
 * switch to "Next · <chapter>" whenever the level ahead begins one. The other
 * lanes are one click away on the map, which both screens also offer.
 *
 * With one track the head is the only member and all four screens behave
 * exactly as they did.
 */
export interface Onward {
  /** Playable now, the continuation first. See the rule above. */
  open: PublicMission[];
  /** Reachable, and in the paid half: real levels behind the one paywall. */
  barred: PublicMission[];
}

export function onward(
  progress: Progress, missions: PublicMission[], from: PublicMission | null = null, signedIn = true,
): Onward {
  // A checkpoint opens nothing, by rule: it requires its world's last level
  // and nothing requires it, so that it never stands in front of anything
  // (docs/checkpoints.md). Asked what it opens, the honest set is empty — and
  // every screen standing on one read that as the end of the path, which is
  // how a learner seven levels into the course was offered "Every level
  // cleared" on the clear overlay.
  //
  // What those screens mean is "where now", and off the path the answer is
  // where the learner had got to on it: the frontier, the same set the map's
  // hero reads. Less the checkpoints in it, this one included — a checkpoint
  // is a side room, not the way on, and a Next button numbered like a level
  // must point at a level.
  if (from && from.track === 'checkpoint') {
    const frontier = onward(progress, missions, null, signedIn);
    const onPath = (m: PublicMission) => m.track !== 'checkpoint';
    return { open: frontier.open.filter(onPath), barred: frontier.barred.filter(onPath) };
  }
  const open: PublicMission[] = [];
  const barred: PublicMission[] = [];
  for (const m of missions) {
    // The candidates: what `from` opens, or — with no `from` — everything not
    // already behind the learner. A cleared level is dropped from the
    // frontier and kept as a successor on purpose, because the two questions
    // mean different things by it: the frontier is where you have got to, and
    // a replay of level 5 still points at level 6.
    if (from ? !m.requires.includes(from.id) : levelProgress(progress, m.id).completed) continue;
    // Offer the chapter assessment once, while leaving the next level open.
    if (from && m.track === 'checkpoint' && levelProgress(progress, m.id).completed) continue;
    const gate = gateFor(progress, m, missions, signedIn);
    if (gate.kind === 'open') {
      open.push(m);
    } else if (gate.kind === 'sequence') {
      // Open when the only thing outstanding is the level being left, per
      // "`from` counts as behind" above. `unresolved` has to be empty too: a
      // prerequisite the catalogue cannot find is a refusal rather than a
      // near miss, and this fails closed the way the gate does (progress.ts).
      if (from && gate.unresolved.length === 0 && gate.missing.every((x) => x.id === from.id)) open.push(m);
    } else if (reachable(progress, m, missions, from)) {
      barred.push(m);
    }
  }
  if (!from) return { open, barred };
  // The chapter's unfinished checkpoint first, then the same lane. This is
  // a recommendation, not a gate: the next level remains playable.
  const checkpoints = open.filter((m) => m.track === 'checkpoint');
  const here = open.filter((m) => m.track === from.track);
  return { open: [...checkpoints, ...here, ...open.filter((m) => m.track !== 'checkpoint' && m.track !== from.track)], barred };
}

/**
 * Every prerequisite behind, with the account left out of it.
 *
 * `gateFor` answers the paywall first and deliberately never computes the
 * prerequisites of a level this browser does not hold — a correct refusal
 * that says nothing about whether it is this learner's turn (lib/entitlement
 * .ts). `barred` needs to know: "the only thing in the way is the money" and
 * "it is not your turn either" are different levels, and only the first is
 * worth pointing a reader at. So this is the same walk `unmet` does, reading
 * the same two helpers so the two answers cannot drift apart, with `from`
 * treated as behind for the reason given above.
 */
function reachable(p: Progress, mission: PublicMission, missions: PublicMission[], from: PublicMission | null): boolean {
  return mission.requires.every((id) => {
    if (from && id === from.id) return true;
    const prerequisite = missions.find((m) => m.id === id);
    return prerequisite ? isBehind(levelProgress(p, prerequisite.id)) : false;
  });
}
